import { FinancialInfo, Proposal } from "@/models/proposal.model";
import { FormValues } from "@/models/proposalForm.model";
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  VStack,
  HStack,
  Input,
  Divider,
  useDisclosure,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { InsuranceCardOverview } from "../../../InsuranceCardOverview";
import InstallmentModal from "./InstallmentModal";
import CopyImage from "./CopyImage";
import { useUserInfo } from "@/contexts/userInfoContext";

interface ResultStepProps {
  data?: FormValues["resultStep"];
  proposal?: Proposal;
  updateData: (values: Partial<FormValues["resultStep"]>) => void;
  handleNext: () => void;
  handleBack: () => void;
}

export default function ResultStep({
  data,
  proposal,
  updateData,
  handleNext,
}: ResultStepProps) {
  const { user } = useUserInfo();
  const [maxAntecipatedYears] = useState<number>(
    Math.min(
      proposal?.anticipatedYears ?? getInvestorsMaxInstallments(),
      getInvestorsMaxInstallments()
    )
  );
  const [initalAntecipatedYears] = useState<number>(
    data?.antecipatedYears ?? maxAntecipatedYears
  );
  const [antecipatedYears, setAntecipatedYears] = useState<number>(
    initalAntecipatedYears
  );
  const [antecipatedYearsError, setAntecipatedYearsError] = useState<
    string | null
  >(null);

  const chosenFinancialOption = proposal?.optionsFinancialInfo?.[0];

  const getIssueAmount = (years: number) => {
    return chosenFinancialOption?.Installments
      ? chosenFinancialOption.Installments.slice(0, years).reduce(
        (acc, installment) => acc + installment.payment,
        0
      )
      : undefined;
  };

  const getReleasedAmount = (years: number) => {
    return data?.chosenFinancialOption?.Installments?.at(years - 1)
      ?.releasedAmount;
  };

  const [maxReleasedAmount] = useState<number>(
    getReleasedAmount(maxAntecipatedYears)!
  );
  const [initialReleasedAmount] = useState<number | undefined>(
    data?.releasedAmount ?? getReleasedAmount(initalAntecipatedYears)
  );
  const initialIssueAmount = getIssueAmount(initalAntecipatedYears);
  const [releasedAmount, setReleasedAmount] = useState<number | undefined>(
    initialReleasedAmount
  );
  const [releasedAmountError, setReleasedAmountError] = useState<string | null>(
    null
  );
  const [issueAmount, setIssueAmount] = useState<number | undefined>(
    initialIssueAmount
  );

  const imageRef = useRef<HTMLDivElement>(null);
  const { isOpen, onClose } = useDisclosure();

  const handleAntecipatedYearsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedValue = parseInt(event.target.value, 10);
    if (event.target.value.startsWith("0")) return;
    validateAntecipatedYearsError(parsedValue);
    setAntecipatedYears(parsedValue);
    updateData({ antecipatedYears: parsedValue });
    updateIof(parsedValue);
    const newReleasedAmount = getReleasedAmount(parsedValue);
    if (newReleasedAmount !== undefined) {
      setReleasedAmount(newReleasedAmount);
      setIssueAmount(getIssueAmount(parsedValue));
      updateData({ releasedAmount: newReleasedAmount });
    }
  };

  const handleReleasedAmountBlur = () => {
    if (releasedAmount !== undefined) {
      let remainingValue = releasedAmount;
      const chosenFinancialInfoOption: FinancialInfo = JSON.parse(
        JSON.stringify(
          proposal?.optionsFinancialInfo?.find(
            (opt) => opt.id == data?.chosenFinancialOption?.id
          )
        )
      );
      const installments = chosenFinancialInfoOption.Installments!;
      const cumulativeAmounts = installments.map(
        ({ releasedAmount }) => releasedAmount
      );
      for (let i = 1; i < installments.length; i++) {
        remainingValue -= cumulativeAmounts[i];
        if (remainingValue < 0) {
          installments[i].releasedAmount += remainingValue;
          break;
        }
      }
      updateData({ installments });
    }
  };

  const handleReleasedAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedValue = parseFloat(
      (Number(e.target.value.replace(/\D/g, "")) / 100).toFixed(2)
    );
    if (isNaN(parsedValue)) return;
    setReleasedAmount(parsedValue);
    if (parsedValue < maxReleasedAmount) {
      updateData({ releasedAmount: parsedValue });
    }
    const installments = chosenFinancialOption?.Installments ?? [];
    validateReleasedAmountError(parsedValue);
    const cumulativeAmounts = installments.map(
      ({ releasedAmount }) => releasedAmount
    );
    const antecipatedYearsIndex = cumulativeAmounts.findIndex(
      (_amount, index) => {
        if (index === 0) return false;
        return cumulativeAmounts[index] >= parsedValue;
      }
    );
    const finalAntecipatedYears =
      antecipatedYearsIndex !== -1
        ? antecipatedYearsIndex + 1
        : getInvestorsMaxInstallments();
    updateIof(finalAntecipatedYears);
    setAntecipatedYears(finalAntecipatedYears);
    updateData({ antecipatedYears: finalAntecipatedYears });
    const fullInstallmentIssueAmount =
      getIssueAmount(finalAntecipatedYears) ?? 0;
    const fractionedIssueAmount =
      antecipatedYearsIndex !== -1 &&
        cumulativeAmounts[antecipatedYearsIndex] !== 0
        ? (fullInstallmentIssueAmount * parsedValue) /
        cumulativeAmounts[antecipatedYearsIndex]
        : fullInstallmentIssueAmount;
    setIssueAmount(fractionedIssueAmount);
  };

  const updateIof = (years: number) => {
    const iof = chosenFinancialOption?.Installments
      ? chosenFinancialOption.Installments.slice(0, years + 1).reduce(
        (acc, installment) => acc + installment.financeTax,
        0
      )
      : undefined;
    if (iof !== undefined) {
      updateData({ iof });
    }
  };

  const validateReleasedAmountError = (amount?: number) => {
    const installments = data?.chosenFinancialOption?.Installments;
    if (!installments?.length || !amount) return;

    const minimalReleasedAmount =
      getReleasedAmount(2)! >
      data?.chosenFinancialOption?.Product?.processor?.investors?.at(0)
        ?.minValue!
        ? getReleasedAmount(2)!
        : data?.chosenFinancialOption?.Product?.processor?.investors?.at(0)
            ?.minValue!;
    const maximalReleasedAmount = getReleasedAmount(maxAntecipatedYears)!;

    const formattedMinimalAmount = BrlCurrencyFormatter(minimalReleasedAmount);
    const formattedMaximalAmount = BrlCurrencyFormatter(maximalReleasedAmount);

    const errorConditions = [
      {
        condition: minimalReleasedAmount > amount,
        message: `O Valor liberado deve ser maior que ${formattedMinimalAmount}`,
      },
      {
        condition: amount > maximalReleasedAmount,
        message: `O Valor liberado deve ser menor que ${formattedMaximalAmount}`,
      },
    ];

    const errorMessage =
      errorConditions.find(({ condition }) => condition)?.message || null;

    setReleasedAmountError(errorMessage);
  };

  function getInvestorsMaxInstallments(): number {
    return proposal?.Processor?.investors?.reduce(function (prev, current) {
      if (!prev.maxInstallments) prev.maxInstallments = 0;
      if (!current.maxInstallments) current.maxInstallments = 0;
      return prev.maxInstallments > current.maxInstallments ? prev : current;
    }).maxInstallments!;
  }

  function getInvestorsMinInstallments(): number {
    return proposal?.Processor?.investors?.reduce(function (prev, current) {
      if (!prev.minInstallments) prev.minInstallments = 0;
      if (!current.minInstallments) current.minInstallments = 0;
      return prev.minInstallments < current.minInstallments ? prev : current;
    }).minInstallments!;
  }

    const validateAntecipatedYearsError = (years: number) =>
      !years ||
      years < getInvestorsMinInstallments() ||
      years > maxAntecipatedYears
        ? setAntecipatedYearsError(
            `O número de anos não pode ser menor que ${
              getInvestorsMinInstallments() ?? 2
            } ou maior que ${maxAntecipatedYears}`
          )
        : setAntecipatedYearsError(null);


  useEffect(() => {
    updateIof(antecipatedYears);
    validateReleasedAmountError(releasedAmount);
    validateAntecipatedYearsError(antecipatedYears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenFinancialOption, antecipatedYears, releasedAmount]);

  return (
    <Flex direction={{ base: "column" }} wrap="wrap" bg="gray.100" minH="100vh">
      <Flex
        w={{ base: "100%", xl: "35%" }}
        mb={{ base: 8, lg: 12 }}
        mt={{ base: 8, lg: 8 }}
        color="gray.600"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={6}
      >
        <Text textAlign="center">
          <strong>{user.name?.split(" ")[0]}</strong>, confira os dados da sua
          simulação
        </Text>
        <Text>Valor liberdo a receber</Text>
        <Heading
          size="4xl"
          color="secondary"
          fontFamily="Lexend Deca"
          fontWeight="500"
          mb={4}
          textAlign={{ base: "center", lg: "left" }}
        >
          {antecipatedYearsError || releasedAmountError
            ? "Valor não disponível"
            : BrlCurrencyFormatter(releasedAmount!)}
        </Heading>
      </Flex>
      <VStack
        w={{ base: "100%", xl: "40%" }}
        spacing={4}
        pr={{ base: "0", lg: "5rem" }}
        mb={{ base: 8, lg: 0 }}
      >
        <Box
          bg="white"
          p={{ base: 4, "2xl": 6 }}
          borderRadius="lg"
          boxShadow="md"
          borderColor="gray.200"
          position="relative"
          w="100%"
          minH="400px"
        >
          <VStack align="start" spacing={1} fontFamily="Lexend Deca">
            <Flex flexDirection="column" gap={2} w="100%">
              <Flex justifyContent="space-between">
                <Text fontSize="sm" color="gray.500">
                  IOF
                </Text>
                <Text fontSize="sm">{BrlCurrencyFormatter(data?.iof!)}</Text>
              </Flex>
              <Divider />
              <Flex justifyContent="space-between">
                <Text fontSize="sm" color="gray.500">
                  TAC
                </Text>
                <Text fontSize="sm">
                  {BrlCurrencyFormatter(
                    chosenFinancialOption?.tacInput! * (issueAmount ?? 0)
                  )}
                </Text>
              </Flex>
              <Divider />
              <Flex justifyContent="space-between" fontSize="sm">
                <Text color="gray.500">Taxa de juros mensal</Text>
                <Text>
                  {(data?.chosenFinancialOption?.monthlyRate! * 100)?.toFixed(
                    2
                  ) ?? "XXXXX"}
                  % a.m. /{" "}
                  {(data?.chosenFinancialOption?.annualRate! * 100)?.toFixed(
                    2
                  ) ?? "XXXXX"}
                  % a.a.
                </Text>
              </Flex>
              <Divider />
              <Flex justifyContent="space-between" fontSize="sm">
                <Text color="gray.500">Custo efetivo total</Text>
                <Text>
                  {(chosenFinancialOption?.monthlyCET! * 100)?.toFixed(2) ?? "XXXXX"}% a.m. /{" "}
                  {(chosenFinancialOption?.annualCET! * 100)?.toFixed(2) ?? "XXXXX"}% a.a.
                </Text>
              </Flex>
              <Divider />
              <Text fontSize="xs" textAlign="center">
                Os resultados acima são da simulação real e é válido como
                proposta.
              </Text>
              {/* <Box gridColumn={{ base: "span 1", md: "2" }}>
                <Button
                  size="sm"
                  colorScheme="primary"
                  color="white"
                  fontFamily="Lexend Deca"
                  fontSize="md"
                  w="100%"
                  onClick={onOpen}
                  isDisabled={!!antecipatedYearsError || !!releasedAmountError}
                >
                  Ver Parcelas
                </Button>
              </Box> */}
            </Flex>
            <Text color="secondary" textAlign="center">
              Selecione quantos anos quer antecipar
            </Text>
          </VStack>
          <HStack
            mt={4}
            spacing={4}
            alignItems="center"
            justify={{ base: "center", lg: "flex-start" }}
          >
            <Input
              value={antecipatedYears}
              onChange={handleAntecipatedYearsChange}
              fontSize={{ base: "2xl", md: "3xl" }}
              width={{ base: "6rem", md: "8rem" }}
              size="lg"
              fontWeight="bold"
              color="secondary"
              fontFamily="Lexend Deca"
              textAlign="center"
              type="number"
              min={1}
            />
            <Slider
              focusThumbOnChange={false}
              onChange={(value) =>
                handleAntecipatedYearsChange({
                  target: { value: value.toString() },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              value={antecipatedYears}
              min={2}
              max={maxAntecipatedYears}
              step={1}
              mr={4}
            >
              <SliderTrack bg="gray.100">
                <SliderFilledTrack bg="secondary" />
              </SliderTrack>
              <SliderThumb boxSize={4} />
            </Slider>
          </HStack>
          {antecipatedYearsError && (
            <Text
              color="red.500"
              mt={2}
              fontSize={{ base: "sm", md: "md" }}
              fontFamily="Lexend Deca"
              textAlign={{ base: "center", lg: "left" }}
            >
              {antecipatedYearsError}
            </Text>
          )}
          <Text mt={2} color="secondary" textAlign="center">
            Deseja alterar o valor do empréstimo?
          </Text>
          <HStack
            mt={4}
            spacing={4}
            alignItems="center"
            justify={{ base: "center", lg: "flex-start" }}
          >
            <Text
              fontSize={{ base: "md", md: "lg" }}
              fontFamily="Lexend Deca"
              color="gray.500"
            >
              Informe o quanto precisa:
            </Text>
            <Input
              value={BrlCurrencyFormatter(releasedAmount)}
              onBlur={handleReleasedAmountBlur}
              onChange={handleReleasedAmountChange}
              fontSize={{ base: "lg", md: "xl" }}
              width={{ base: "80%", md: "10rem", lg: "12rem" }}
              size="lg"
              fontWeight="bold"
              color="secondary"
              fontFamily="Lexend Deca"
              textAlign="center"
              type="tel"
              placeholder="R$ 0,00"
            />
          </HStack>
          {releasedAmountError && (
            <Text
              color="red.500"
              mt={2}
              fontSize={{ base: "sm", md: "md" }}
              fontFamily="Lexend Deca"
              textAlign={{ base: "center", lg: "left" }}
            >
              {releasedAmountError}
            </Text>
          )}

          <Flex
            pt={{ base: "2rem", lg: "4rem" }}
            justifyContent="center"
            w="100%"
            gap={3}
            flexDirection={{ base: "column", sm: "row" }}
          >
            {/* <Button
              colorScheme="secondary"
              variant="outline"
              w={{ base: "100%", sm: "35%" }}
              fontFamily="Lexend Deca"
              fontSize="sm"
              onClick={handleBack}
            >
              Voltar
            </Button> */}
            <Button
              bg="primary"
              color="white"
              fontFamily="Lexend Deca"
              fontSize="sm"
              onClick={handleNext}
              isDisabled={!!antecipatedYearsError || !!releasedAmountError}
              w={{ base: "100%", sm: "35%" }}
            >
              Sim, quero contratar o empréstimo
            </Button>
          </Flex>
        </Box>
        {chosenFinancialOption?.tacInput ? null : <InsuranceCardOverview />}
      </VStack>
      <Box
        ref={imageRef}
        position="absolute"
        left="-9999px"
        top={0}
        width="400px"
        minH="400px"
        overflow="hidden"
      >
        <CopyImage
          releasedAmount={releasedAmount!}
          document={proposal?.NaturalPerson?.document!}
          antecipatedYears={antecipatedYears!}
          monthlyRate={chosenFinancialOption?.monthlyRate!}
          issueAmount={issueAmount!}
        />
      </Box>
      <InstallmentModal
        isOpen={isOpen}
        onClose={onClose}
        installments={
          chosenFinancialOption!.Installments!.slice(0, antecipatedYears ?? 10)!
        }
      />
    </Flex>
  );
}
